import { memo } from 'react'

import { BackButton } from '~/shared/ui/BackButton'
import { IconButton } from '~/shared/ui/IconButton'
import { ProgressSegments } from '~/shared/ui/ProgressSegments'
import { HStack } from '~/shared/ui/HStack'
import { VStack } from '~/shared/ui/VStack'
import { Text } from '~/shared/ui/Text'

import type { ScreenHeaderProps } from './types'

import styles from './ScreenHeader.module.scss'

const NOOP = () => {}

// Подпись только для скринридера, как у BackButton: видимого текста у кнопки нет,
// а `shared` не имеет права импортировать `modules/lunya/copy` (CLAUDE.md правило 1).
const CLOSE_LABEL = 'Закрыть'

/**
 * Шапка экрана: оверлайн + заголовок + слот справа + опциональная своя кнопка «назад».
 * На внутренних экранах (без overline) заголовок рендерится H1 — по спеке 01 §5.
 * Режим progress (спека 01 §8) заменяет обычную шапку на строку шага + ProgressSegments.
 */
export const ScreenHeader = memo(function ScreenHeader(props: ScreenHeaderProps) {
  const { overline, title, rightSlot, onBack, progress } = props

  if (progress) {
    const { flowLabel, total, current, onClose } = progress

    return (
      <div className={styles.root}>
        <HStack justify='BETWEEN' align='CENTER' gap='SMALL_L'>
          <HStack gap='SMALL_M' align='CENTER'>
            {/* Крайние слоты флоу резервируют ТОЛЬКО высоту строки: без резерва шаг без
                «назад» (или без «выйти») даёт строку ниже, полоса прогресса встаёт выше,
                чем на соседних шагах, и переход между шагами дёргается по вертикали.
                Ширину резерв не занимает — `width: 0` плюс гашение gap отрицательным
                margin, иначе на шаге без кнопки оверлайн уезжал бы вправо на пустое место.
                Место держит сама кнопка под `visibility: hidden` — так высота слота не может
                разъехаться с реальной кнопкой, а скрытая так кнопка не попадает ни в
                таб-порядок, ни в дерево доступности. */}
            <div
              className={styles.edgeSlot}
              data-side='start'
              data-reserved={onBack ? undefined : true}
            >
              <BackButton onPress={onBack ?? NOOP} />
            </div>
            <Text variant='OVERLINE' color='ACCENT_SOFT'>
              {flowLabel}
            </Text>
          </HStack>
          <HStack gap='SMALL_M' align='CENTER'>
            <div className={styles.step}>
              <Text variant='CAPTION' color='ACCENT'>{`шаг ${current} из ${total}`}</Text>
            </div>
            <div
              className={styles.edgeSlot}
              data-side='end'
              data-reserved={onClose ? undefined : true}
            >
              <IconButton
                iconName='close'
                size='S'
                variant='GHOST'
                ariaLabel={CLOSE_LABEL}
                onPress={onClose ?? NOOP}
              />
            </div>
          </HStack>
        </HStack>
        <ProgressSegments total={total} current={current} />
      </div>
    )
  }

  return (
    <HStack justify='BETWEEN' align='CENTER' gap='SMALL_L'>
      <HStack gap='SMALL_M' align='CENTER'>
        {onBack && <BackButton onPress={onBack} />}
        <VStack gap='SMALL_XS'>
          {overline && (
            <Text variant='OVERLINE' color='ACCENT_SOFT'>
              {overline}
            </Text>
          )}
          <Text variant={overline ? 'H2' : 'H1'}>{title}</Text>
        </VStack>
      </HStack>
      {rightSlot}
    </HStack>
  )
})
